import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Tabs, TabsList, TabsTrigger } from '@ais-ui/ui-core';

import {
  DrawCircleField,
  DrawLineField,
  DrawPointField,
  DrawPolygonField,
} from '../../../Shapes/DrawFormFields';
import { useDrawWithViewMode } from '../../../../hooks/useDrawWithViewMode';
import { useMutateReportContext } from '../CreateReportTemplate/CreateReportContext';

type DrawTab = 'polygon' | 'linestring' | 'point' | 'circle';

interface InitialDrawState {
  tab: DrawTab;
  maxPoints: number | undefined;
}

/**
 * Derives the initial draw tab (and point limit) from the allowed geometry
 * types. When more than one type is allowed the user picks via the tabs, so we
 * default to polygon; with a single allowed type we pre-select it.
 */
const deriveInitialDrawState = (lowerCaseTypes: string[]): InitialDrawState => {
  if (lowerCaseTypes.length > 1) {
    return { tab: 'polygon', maxPoints: undefined };
  }
  if (lowerCaseTypes.includes('linestring')) {
    return { tab: 'linestring', maxPoints: undefined };
  }
  if (lowerCaseTypes.includes('twopointlinestring')) {
    return { tab: 'linestring', maxPoints: 2 };
  }
  if (lowerCaseTypes.includes('point')) {
    return { tab: 'point', maxPoints: undefined };
  }
  if (lowerCaseTypes.includes('circle')) {
    return { tab: 'circle', maxPoints: undefined };
  }
  return { tab: 'polygon', maxPoints: undefined };
};

interface AdHocShapeSelectProps {
  allowedGeometryTypes: string[];
  geometryField: string;
  form: UseFormReturn<any>;
  shouldAutoTrigger: boolean;
  onAutoTriggerComplete: () => void;
}

/**
 * Renders the in-dialog drawing UI for a single shape field (instant shape
 * mode). Shared by both the create wizard (ShapeStep) and the clone form
 * (ShapeFormField) so the drawing behaviour - including the auto-trigger -
 * lives in exactly one place.
 *
 * The drawn geometry is written to `geometryField` via the form. Callers are
 * responsible for only auto-triggering the appropriate field (e.g. the first
 * shape field) so multi-area reports don't register competing draw-accept
 * handlers on the shared drawing context.
 */
export const AdHocShapeSelect = ({
  allowedGeometryTypes,
  geometryField,
  form,
  shouldAutoTrigger,
  onAutoTriggerComplete,
}: AdHocShapeSelectProps) => {
  const lowerCaseTypes = useMemo(
    () => allowedGeometryTypes.map((type) => type.toLowerCase()),
    [allowedGeometryTypes],
  );

  const initialDrawState = useMemo(
    () => deriveInitialDrawState(lowerCaseTypes),
    [lowerCaseTypes],
  );

  const [tab, setTab] = useState<DrawTab>(initialDrawState.tab);
  const maxPoints = initialDrawState.maxPoints;
  const { setShowDialog, instantShapeData } = useMutateReportContext();

  const { onStartDrawingFromDialog } = useDrawWithViewMode(
    form,
    setShowDialog,
    geometryField,
  );

  // Watch the form field value to get reactive updates
  const formGeometry = form.watch(geometryField);

  // Use instantShapeData as fallback if form doesn't have the geometry yet
  const geometry = formGeometry || instantShapeData?.[geometryField];

  // Auto-trigger drawing when shouldAutoTrigger is true
  useEffect(() => {
    if (shouldAutoTrigger && !geometry) {
      // Trigger drawing based on the current tab
      if (tab === 'polygon') {
        onStartDrawingFromDialog('Polygon', null);
      } else if (tab === 'linestring') {
        onStartDrawingFromDialog('LineString', null);
      } else if (tab === 'point') {
        onStartDrawingFromDialog('Point', null);
      } else if (tab === 'circle') {
        onStartDrawingFromDialog('Circle', null);
      }
      onAutoTriggerComplete();
    }
  }, [
    shouldAutoTrigger,
    geometry,
    tab,
    onStartDrawingFromDialog,
    onAutoTriggerComplete,
  ]);

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as DrawTab)}
      defaultValue="polygon"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      {lowerCaseTypes.length > 1 ? (
        <TabsList className="w-fit">
          {lowerCaseTypes.includes('polygon') && (
            <TabsTrigger value="polygon">Polygon</TabsTrigger>
          )}
          {lowerCaseTypes.includes('linestring') && (
            <TabsTrigger value="linestring">Line</TabsTrigger>
          )}
          {lowerCaseTypes.includes('point') && (
            <TabsTrigger value="point">Point</TabsTrigger>
          )}
          {lowerCaseTypes.includes('circle') && (
            <TabsTrigger value="circle">Circle</TabsTrigger>
          )}
        </TabsList>
      ) : null}
      {tab === 'polygon' && (
        <DrawPolygonField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPolygonField>
      )}
      {tab === 'linestring' && (
        <DrawLineField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          maxPoints={maxPoints}
          color={'blue'}
        ></DrawLineField>
      )}
      {tab === 'point' && (
        <DrawPointField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPointField>
      )}
      {tab === 'circle' && (
        <DrawCircleField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawCircleField>
      )}
    </Tabs>
  );
};
